import { SanitizeHTMLString } from "@circle-react/components/shared/uikit/SanitizeContent";
import { Box } from "@circleco/compass/components/Box";
import { Divider } from "@circleco/compass/components/Divider";
import { defer } from "lodash";
import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

import type { CompassPromptMenuHandle } from "../CompassPromptMenu";
import { CompassPromptMenu } from "../CompassPromptMenu";
import { useAiBlockContext } from "../hooks/useAiBlockContext";
import { useEditionsStream } from "../hooks/useEditionsStream";
import { useFormSubmitDisabler } from "../hooks/useFormSubmitDisabler";
import { LoaderPrompt } from "../LoaderPrompt";
import { AiIcon } from "../Prompt/AiIcon";
import { Input } from "../Prompt/Input";
import {
  createPromptKeydownHandler,
  useNativeInputKeydown,
} from "../useNativeInputKeydown";

const EDIT_OPTION_KEYS = [
  "improve_writing",
  "fix_text",
  "make_shorter",
  "make_longer",
  "simplify_language",
  "summarize",
  "explain",
  "translate_spanish",
  "translate_french",
  "translate_dutch",
  "translate_italian",
  "translate_portuguese",
  "translate_english",
] as const;

const EDITIONS_I18N_ROOT =
  "tiptap.extensions.ai_copilot.prompt_options.editions";

const editValue = (key: string) =>
  key.startsWith("translate") ? "translate" : key;

export const CompassEditPrompt = () => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const promptMenuRef = useRef<CompassPromptMenuHandle>(null);
  const responseMenuRef = useRef<CompassPromptMenuHandle>(null);

  const [userPrompt, setUserPrompt] = useState("");
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [preview, setPreview] = useState("");
  const [promptType, setPromptType] = useState("");

  const { createEdition, isLoading } = useEditionsStream();
  const { node, editor, cleanupNode } = useAiBlockContext();
  const { range } = node.attrs;
  const selectedText = range
    ? editor.state.doc.textBetween(range.from, range.to, "\n", ". ")
    : "";

  useFormSubmitDisabler({ isLoading });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const shouldShowResponseOptions = !isLoading && Boolean(preview);

  const performEdit = (type: string, prompt: string) => {
    if (!prompt || isInputDisabled) {
      return;
    }

    setPreview("");
    setIsInputDisabled(true);

    void createEdition({
      type: type || EDIT_OPTION_KEYS[0],
      user_prompt: prompt,
      selected_text: selectedText,
      onMessage: (data: string) => setPreview(prev => prev + data),
      onOpen: () => setUserPrompt(""),
      onClose: () => {
        setUserPrompt("");
        setPromptType("");
        setIsInputDisabled(false);
        defer(() => inputRef.current?.focus());
      },
      onError: () => {
        setPromptType("");
        setIsInputDisabled(false);
        defer(() => inputRef.current?.focus());
      },
    });
  };

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    performEdit(promptType, userPrompt);
  };

  const selectPromptOption = (index: number) => {
    const key = EDIT_OPTION_KEYS[index];
    const label = t([EDITIONS_I18N_ROOT, key]);
    setUserPrompt(label);
    setPromptType(editValue(key));
    performEdit(editValue(key), label);
  };

  const endPos = editor.state.selection.$to.after();
  const responseOptions = [
    {
      id: "replace",
      label: t("tiptap.replace_selection"),
      run: () => {
        if (!range) {
          return;
        }
        editor.commands.setTextSelection(range);
        editor.commands.insertContentAt(range, preview);
        cleanupNode();
      },
    },
    {
      id: "insert_below",
      label: t("tiptap.insert_bellow"),
      run: () => {
        editor.commands.insertContentAt(endPos, preview);
        cleanupNode();
      },
    },
  ];

  const editOptions = EDIT_OPTION_KEYS.map(key => ({
    id: key,
    label: t([EDITIONS_I18N_ROOT, key]),
  }));

  useNativeInputKeydown(
    inputRef,
    createPromptKeydownHandler({
      getMenu: () => promptMenuRef.current ?? responseMenuRef.current,
      getUserPrompt: () => userPrompt,
      onEscape: cleanupNode,
      onSubmit: handleSubmit,
    }),
    isLoading,
  );

  return (
    <>
      <Box className="overflow-hidden p-0">
        {preview && (
          <>
            <div className="text-body-sm select-none px-4 py-3 [&_:first-child]:mt-0 [&_:last-child]:mb-0">
              <SanitizeHTMLString content={preview} />
            </div>
            <Divider />
          </>
        )}
        {isLoading ? (
          <LoaderPrompt />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="bg-primary flex items-start gap-3 px-4 py-2.5">
              <AiIcon />
              <Input
                ref={inputRef}
                id={`input-${node.attrs?.id}`}
                placeholder={node.attrs?.placeholder}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  setUserPrompt(event.target.value)
                }
                value={userPrompt}
              />
            </div>
          </form>
        )}
      </Box>
      {!userPrompt && !preview && (
        <CompassPromptMenu
          ref={promptMenuRef}
          label={t("tiptap.extensions.ai_copilot.prompt_options.title")}
          options={editOptions}
          onSelect={selectPromptOption}
        />
      )}
      {shouldShowResponseOptions && !userPrompt && (
        <CompassPromptMenu
          ref={responseMenuRef}
          options={responseOptions}
          onSelect={index => responseOptions[index].run()}
        />
      )}
    </>
  );
};
